import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { fetchAlerts, markAlertRead, markAllAlertsRead } from '@/lib/admin/alerts';
import type { AlertSeverity, AlertType, SubscriptionAlert } from '@/types/admin';
import { cn } from '@/lib/cn';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  INFO: 'bg-sky-500',
  WARNING: 'bg-amber-500',
  CRITICAL: 'bg-rose-500',
};

const ALL_SEVERITIES: AlertSeverity[] = ['INFO', 'WARNING', 'CRITICAL'];
const ALL_TYPES: AlertType[] = [
  'CREATED', 'PLAN_CHANGED', 'STATUS_CHANGED',
  'TRIAL_STARTED', 'TRIAL_ENDING_SOON', 'TRIAL_ENDED',
  'CANCELED', 'REACTIVATED', 'RENEWED',
  'PAYMENT_FAILED', 'PAYMENT_SUCCEEDED', 'WEBHOOK_RECEIVED',
];

function alertLink(a: SubscriptionAlert): string {
  if (a.user) return `/admin/users/${a.user.id}`;
  return '/admin/subscriptions';
}

export default function AlertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const severity = (searchParams.get('severity') ?? '') as AlertSeverity | '';
  const type = (searchParams.get('type') ?? '') as AlertType | '';
  const unread = searchParams.get('unread') === '1';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const page = Number(searchParams.get('page')) || 1;
  const perPage = 25;

  const [fromInput, setFromInput] = useState(from);
  const [toInput, setToInput] = useState(to);
  useEffect(() => { setFromInput(from); setToInput(to); }, [from, to]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'alerts', { severity, type, unread, from, to, page }],
    queryFn: () => fetchAlerts({
      severity: severity || undefined,
      type: type || undefined,
      unread: unread || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      perPage,
    }),
    placeholderData: keepPreviousData,
  });

  const markOne = useMutation({
    mutationFn: (id: string) => markAlertRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'alerts'] }),
  });

  const markAll = useMutation({
    mutationFn: () => markAllAlertsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'alerts'] }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.alerts.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('admin.alerts.subtitle')}</p>
        </div>
        <button
          type="button"
          className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          disabled={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          {t('admin.alerts.markAllRead')}
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600">{t('admin.columns.severity') ?? 'Severity'}</label>
            <select
              className="mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={severity}
              onChange={(e) => setParam('severity', e.target.value)}
            >
              <option value="">{t('admin.filters.allSeverities')}</option>
              {ALL_SEVERITIES.map((s) => (
                <option key={s} value={s}>{t(`admin.alerts.severity.${s}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">{t('admin.columns.type')}</label>
            <select
              className="mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={type}
              onChange={(e) => setParam('type', e.target.value)}
            >
              <option value="">{t('admin.filters.allTypes')}</option>
              {ALL_TYPES.map((typ) => (
                <option key={typ} value={typ}>{t(`admin.alerts.type.${typ}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">{t('admin.columns.from') ?? 'From'}</label>
            <input
              type="date"
              className="mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={fromInput}
              onChange={(e) => { setFromInput(e.target.value); setParam('from', e.target.value); }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">{t('admin.columns.to') ?? 'To'}</label>
            <input
              type="date"
              className="mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={toInput}
              onChange={(e) => { setToInput(e.target.value); setParam('to', e.target.value); }}
            />
          </div>
          <label className="ml-auto flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={unread}
              onChange={(e) => setParam('unread', e.target.checked ? '1' : '')}
            />
            {t('admin.alerts.showUnreadOnly') ?? 'Show only unread'}
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2">{t('admin.columns.severity') ?? 'Severity'}</th>
              <th className="px-4 py-2">{t('admin.columns.type')}</th>
              <th className="px-4 py-2">{t('admin.columns.message') ?? 'Message'}</th>
              <th className="px-4 py-2">{t('admin.columns.change') ?? 'Change'}</th>
              <th className="px-4 py-2">{t('admin.columns.user')}</th>
              <th className="px-4 py-2">{t('admin.columns.organization') ?? 'Organization'}</th>
              <th className="px-4 py-2">{t('admin.columns.when')}</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.loading')}</td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">{t('admin.alerts.noAlerts')}</td></tr>
            )}
            {items.map((a) => (
              <tr
                key={a.id}
                className={cn('cursor-pointer hover:bg-gray-50', !a.readAt && 'bg-indigo-50/30')}
                onClick={() => {
                  if (!a.readAt) markOne.mutate(a.id);
                  navigate(alertLink(a));
                }}
              >
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2">
                    <span className={cn('h-2 w-2 rounded-full', SEVERITY_DOT[a.severity])} />
                    <span className="text-xs font-semibold uppercase text-gray-600">
                      {t(`admin.alerts.severity.${a.severity}`)}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3 text-xs font-semibold uppercase text-gray-700">
                  {t(`admin.alerts.type.${a.type}`)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800">{a.message}</td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {a.oldValue || a.newValue ? (
                    <span>{a.oldValue ?? '—'} → {a.newValue ?? '—'}</span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{a.user?.email ?? '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{a.organization?.name ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500" title={new Date(a.createdAt).toLocaleString()}>
                  {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                </td>
                <td className="px-4 py-3 text-right">
                  {!a.readAt && (
                    <button
                      type="button"
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-emerald-600"
                      aria-label={t('admin.alerts.markRead')}
                      onClick={(e) => { e.stopPropagation(); markOne.mutate(a.id); }}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
          <span>{t('common.showing')} {items.length} {t('common.of')} {total}</span>
          <div className="flex items-center gap-2">
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>‹</button>
            <span>{page} / {totalPages}</span>
            <button className="rounded border border-gray-200 px-3 py-1 disabled:opacity-50" disabled={page >= totalPages} onClick={() => setParam('page', String(page + 1))}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
